/** Pedido (anotado en la OC) vs lo cobrado (recepciones). Promedio ponderado por cantidad. */

export type OrderedVsReceivedLine = {
  productId: string | null;
  productName: string;
  quantity: number;
  unitCost: number | null;
};

export type OrderedVsReceivedRow = {
  productId: string | null;
  productName: string;
  orderedUnitCost: number | null;
  receivedUnitCost: number | null;
};

const COST_DIFF_EPSILON = 0.01;

function productKey(productId: string | null, productName: string): string {
  if (productId != null && productId !== '') return `id:${productId}`;
  return `name:${productName}`;
}

export function weightedUnitCost(lines: OrderedVsReceivedLine[]): number | null {
  let qty = 0;
  let total = 0;
  for (const line of lines) {
    if (line.unitCost == null || !(line.quantity > 0)) continue;
    qty += line.quantity;
    total += line.quantity * line.unitCost;
  }
  if (qty <= 0) return null;
  return total / qty;
}

export function aggregateOrderedVsReceived(
  orderedLines: OrderedVsReceivedLine[],
  receivedLines: OrderedVsReceivedLine[],
): OrderedVsReceivedRow[] {
  const names = new Map<string, string>();
  const ids = new Map<string, string | null>();
  const orderedBy = new Map<string, OrderedVsReceivedLine[]>();
  const receivedBy = new Map<string, OrderedVsReceivedLine[]>();

  const push = (
    bucket: Map<string, OrderedVsReceivedLine[]>,
    line: OrderedVsReceivedLine,
  ) => {
    const key = productKey(line.productId, line.productName);
    names.set(key, line.productName || 'Sin nombre');
    if (!ids.has(key)) ids.set(key, line.productId);
    const list = bucket.get(key) ?? [];
    list.push(line);
    bucket.set(key, list);
  };

  for (const line of orderedLines) push(orderedBy, line);
  for (const line of receivedLines) push(receivedBy, line);

  const keys = new Set([...orderedBy.keys(), ...receivedBy.keys()]);
  return [...keys].map((key) => ({
    productId: ids.get(key) ?? null,
    productName: names.get(key) ?? 'Sin nombre',
    orderedUnitCost: weightedUnitCost(orderedBy.get(key) ?? []),
    receivedUnitCost: weightedUnitCost(receivedBy.get(key) ?? []),
  }));
}

/** Filas donde ambos costos existen y no coinciden. */
export function orderedVsReceivedDiffs(
  rows: OrderedVsReceivedRow[],
  epsilon = COST_DIFF_EPSILON,
): OrderedVsReceivedRow[] {
  return rows.filter((row) => {
    if (row.orderedUnitCost == null || row.receivedUnitCost == null) return false;
    return Math.abs(row.orderedUnitCost - row.receivedUnitCost) > epsilon;
  });
}
